import { useEffect, useState, useCallback, useRef, type ChangeEvent, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { equipmentApi, resolveImageUrl, type EquipmentItem } from "@/lib/api";
import { type FormStatus } from "@/lib/types";
import Footer from "@/pages/home/components/Footer";
import ScrollReveal from "@/components/ScrollReveal";

export default function EquipmentAdminPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<EquipmentItem[]>([]);
  const [fetching, setFetching] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [model, setModel] = useState("");
  const [engine, setEngine] = useState("");
  const [powerKw, setPowerKw] = useState("");
  const [powerPs, setPowerPs] = useState("");
  const [maxBlades, setMaxBlades] = useState("");
  const [maxInputDiameter, setMaxInputDiameter] = useState("");
  const [screens, setScreens] = useState("");
  const [mobility, setMobility] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [description, setDescription] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);

  const [formStatus, setFormStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadItems = useCallback(async () => {
    setFetching(true);
    setFetchError("");
    try {
      const { data } = await equipmentApi.list();
      setItems(data || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "장비 목록을 불러오지 못했습니다.";
      setFetchError(msg);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setManufacturer("");
    setModel("");
    setEngine("");
    setPowerKw("");
    setPowerPs("");
    setMaxBlades("");
    setMaxInputDiameter("");
    setScreens("");
    setMobility("");
    setImageFile(null);
    setImagePreview("");
    setDescription("");
    setFormStatus("idle");
    setFormError("");
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const startEdit = (item: EquipmentItem) => {
    setEditingId(item.id);
    setName(item.name);
    setManufacturer(item.manufacturer);
    setModel(item.model);
    setEngine(item.engine);
    setPowerKw(item.power_kw);
    setPowerPs(item.power_ps);
    setMaxBlades(item.max_blades);
    setMaxInputDiameter(item.max_input_diameter);
    setScreens(item.screens);
    setMobility(item.mobility);
    setImageFile(null);
    setImagePreview(item.image || "");
    setDescription(item.description);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      setFormStatus("error");
      setFormError("JPG, PNG, WEBP, GIF 형식의 이미지만 업로드할 수 있습니다.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setFormStatus("error");
      setFormError("이미지 파일 크기는 10MB 이하여야 합니다.");
      return;
    }

    setImageFile(file);
    setFormStatus("idle");
    setFormError("");

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setFormStatus("error");
      setFormError("장비명을 입력해 주세요.");
      return;
    }

    setFormStatus("submitting");
    setFormError("");
    setUploadProgress(0);

    try {
      let imageUrl = imagePreview;

      if (imageFile) {
        setUploadProgress(30);
        imageUrl = await equipmentApi.uploadImage(imageFile);
        setUploadProgress(80);
      }

      const payload = {
        name: name.trim(),
        manufacturer: manufacturer.trim(),
        model: model.trim(),
        engine: engine.trim(),
        power_kw: powerKw.trim(),
        power_ps: powerPs.trim(),
        max_blades: maxBlades.trim(),
        max_input_diameter: maxInputDiameter.trim(),
        screens: screens.trim(),
        mobility: mobility.trim(),
        image: imageUrl,
        description: description.trim(),
      };

      if (editingId) {
        await equipmentApi.update(editingId, payload);
      } else {
        await equipmentApi.create(payload);
      }

      setUploadProgress(100);
      setFormStatus("success");
      resetForm();
      loadItems();
      setTimeout(() => setFormStatus("idle"), 2000);
    } catch (err: unknown) {
      setFormStatus("error");
      const msg = err instanceof Error ? err.message : "저장에 실패했습니다.";
      setFormError(msg);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (item: EquipmentItem) => {
    if (!confirm(`"${item.name}" 장비를 삭제하시겠습니까?`)) return;
    try {
      await equipmentApi.remove(item.id);
      loadItems();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "삭제에 실패했습니다.");
    }
  };

  return (
    <>
      <main className="min-h-screen bg-background-50 pt-20">
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <div className="mb-6">
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-background-200 hover:border-primary-400 hover:bg-primary-50 text-foreground-600 hover:text-primary-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                홈으로 돌아가기
              </button>
            </div>
            <div className="text-center mb-14">
              <ScrollReveal direction="up" delay={100}>
                <div className="inline-flex items-center gap-2 text-primary-700 text-xs tracking-[0.3em] uppercase font-medium mb-4">
                  <span className="w-8 h-px bg-primary-500"></span>
                  Equipment Admin
                </div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                  장비 정보 관리
                </h1>
                <p className="mt-4 text-foreground-700 text-base max-w-xl mx-auto">
                  보유 장비의 상세 정보를 등록하고 관리하는 페이지입니다.
                  등록한 장비는 메인 페이지 장비 정보 섹션에 표시됩니다.
                </p>
              </ScrollReveal>
            </div>

            <div className="rounded-lg bg-background-100/60 border border-background-200/70 p-6 md:p-10 mb-12">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                {editingId ? "장비 정보 수정" : "새 장비 등록"}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground-800 mb-2">
                      장비명 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      type="text"
                      placeholder="예) JENZ HEM 820 DL 대형 우드 칩핑기"
                      className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">제조사</label>
                    <input value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} type="text" placeholder="예) 독일 JENZ" className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">모델명</label>
                    <input value={model} onChange={(e) => setModel(e.target.value)} type="text" placeholder="예) HEM 820 DL" className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">엔진</label>
                    <input value={engine} onChange={(e) => setEngine(e.target.value)} type="text" placeholder="예) Mercedes-BENZ OM502LA" className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">출력 (kW)</label>
                    <input value={powerKw} onChange={(e) => setPowerKw(e.target.value)} type="text" placeholder="예) 473 kW" className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">출력 (PS)</label>
                    <input value={powerPs} onChange={(e) => setPowerPs(e.target.value)} type="text" placeholder="예) 644 PS" className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">최대 칼날 수</label>
                    <input value={maxBlades} onChange={(e) => setMaxBlades(e.target.value)} type="text" placeholder="예) 12 (최대 24)" className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">최대 투입 지름</label>
                    <input value={maxInputDiameter} onChange={(e) => setMaxInputDiameter(e.target.value)} type="text" placeholder="예) 800 mm" className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">보유 스크린</label>
                    <input value={screens} onChange={(e) => setScreens(e.target.value)} type="text" placeholder="예) 40×40 · 50×50 · 60×60 · 70×70" className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">이동성</label>
                    <input value={mobility} onChange={(e) => setMobility(e.target.value)} type="text" placeholder="예) 견인식 · 전국 현장 대응" className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground-800 mb-2">장비 이미지</label>
                    <div className="flex flex-col gap-3">
                      {!imagePreview ? (
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          onDragOver={(e) => { e.preventDefault(); }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const file = e.dataTransfer.files?.[0];
                            if (file && fileInputRef.current) {
                              const dt = new DataTransfer();
                              dt.items.add(file);
                              fileInputRef.current.files = dt.files;
                              handleFileChange({ target: { files: dt.files } } as unknown as ChangeEvent<HTMLInputElement>);
                            }
                          }}
                          className="relative flex flex-col items-center justify-center gap-3 p-10 rounded-md border-2 border-dashed border-background-300 hover:border-primary-400 hover:bg-primary-50/50 bg-background-50 transition-colors cursor-pointer"
                        >
                          <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                            <i className="ri-image-add-line text-2xl text-primary-600 w-6 h-6 flex items-center justify-center"></i>
                          </div>
                          <div className="text-center">
                            <p className="text-sm font-medium text-foreground-800">
                              클릭 또는 드래그하여 이미지 업로드
                            </p>
                            <p className="text-xs text-foreground-500 mt-1">
                              JPG, PNG, WEBP, GIF · 최대 10MB
                            </p>
                          </div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            onChange={handleFileChange}
                            className="hidden"
                          />
                        </div>
                      ) : (
                        <div className="relative rounded-md overflow-hidden border border-background-200">
                          <img
                            src={resolveImageUrl(imagePreview)}
                            alt="장비 이미지 미리보기"
                            className="w-full h-56 object-cover object-top"
                          />
                          <button
                            type="button"
                            onClick={removeImage}
                            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-foreground-950/60 hover:bg-foreground-950/80 text-background-50 flex items-center justify-center transition-colors cursor-pointer"
                          >
                            <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="absolute bottom-3 left-3 px-3 py-1.5 rounded-md bg-background-50/90 hover:bg-background-50 text-foreground-700 text-xs font-medium flex items-center gap-1.5 transition-colors cursor-pointer"
                          >
                            <i className="ri-refresh-line w-3 h-3 flex items-center justify-center"></i>
                            이미지 변경
                          </button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            onChange={handleFileChange}
                            className="hidden"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground-800 mb-2">설명</label>
                    <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} maxLength={500} placeholder="장비에 대한 상세 설명을 입력해 주세요." className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400 resize-none"></textarea>
                  </div>
                </div>

                {uploadProgress > 0 && formStatus === "submitting" && (
                  <div className="w-full bg-background-200/70 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                )}

                {formStatus === "success" && (
                  <div className="p-4 rounded-md bg-primary-50 border border-primary-200 text-primary-800 text-sm flex items-start gap-2">
                    <i className="ri-check-double-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    장비 정보가 성공적으로 저장되었습니다.
                  </div>
                )}
                {formStatus === "error" && (
                  <div className="p-4 rounded-md bg-accent-100 border border-accent-300 text-accent-900 text-sm flex items-start gap-2">
                    <i className="ri-error-warning-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    {formError || "저장에 실패했습니다."}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={formStatus === "submitting"}
                    className="px-8 py-3.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {formStatus === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                        저장 중...
                      </>
                    ) : (
                      <>
                        {editingId ? "수정 완료" : "장비 등록"}
                        <i className="ri-upload-cloud-2-line w-4 h-4 flex items-center justify-center"></i>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-3.5 rounded-md border border-background-300 text-foreground-700 hover:bg-background-100 font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    초기화
                  </button>
                </div>
              </form>
            </div>

            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold text-foreground-950">
                  등록된 장비 목록
                </h2>
                <span className="text-sm text-foreground-500">총 {items.length}개</span>
              </div>

              {fetching && (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 w-8 h-8 flex items-center justify-center"></i>
                  <span className="text-foreground-500 text-sm">목록을 불러오는 중...</span>
                </div>
              )}

              {!fetching && fetchError && (
                <div className="text-center py-10">
                  <div className="inline-flex flex-col items-center gap-3 p-6 rounded-lg bg-accent-50 border border-accent-200">
                    <i className="ri-error-warning-line text-2xl text-accent-600 w-6 h-6 flex items-center justify-center"></i>
                    <p className="text-accent-800 text-sm">{fetchError}</p>
                    <button onClick={loadItems} className="px-4 py-2 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium cursor-pointer transition-colors">다시 시도</button>
                  </div>
                </div>
              )}

              {!fetching && !fetchError && items.length === 0 && (
                <div className="text-center py-10 rounded-lg bg-background-100 border border-background-200">
                  <i className="ri-tools-line text-3xl text-foreground-400 w-8 h-8 flex items-center justify-center mx-auto mb-3"></i>
                  <p className="text-foreground-600 text-sm">등록된 장비가 없습니다.</p>
                </div>
              )}

              {!fetching && !fetchError && items.length > 0 && (
                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.id} className="rounded-lg border border-background-200/70 overflow-hidden bg-background-50">
                      <div className="flex flex-col md:flex-row">
                        {item.image && (
                          <div className="w-full md:w-48 h-36 md:h-auto overflow-hidden bg-background-100 flex-shrink-0">
                            <img src={resolveImageUrl(item.image)} alt={item.name} className="w-full h-full object-cover object-top" />
                          </div>
                        )}
                        <div className="p-5 flex-1 flex flex-col justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="text-base font-semibold text-foreground-950">{item.name}</h3>
                              <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary-100 text-primary-800">{item.manufacturer} {item.model}</span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-foreground-600">
                              <span>출력: {item.power_kw} / {item.power_ps}</span>
                              <span>엔진: {item.engine}</span>
                              <span>칼날: {item.max_blades}</span>
                              <span>투입 지름: {item.max_input_diameter}</span>
                              <span className="col-span-2">스크린: {item.screens}</span>
                              <span className="col-span-2">이동성: {item.mobility}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-background-100">
                            <button onClick={() => startEdit(item)} className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer flex items-center gap-1">
                              <i className="ri-edit-line w-3 h-3 flex items-center justify-center"></i>수정
                            </button>
                            <button onClick={() => handleDelete(item)} className="text-xs text-accent-600 hover:text-accent-700 font-medium cursor-pointer flex items-center gap-1">
                              <i className="ri-delete-bin-line w-3 h-3 flex items-center justify-center"></i>삭제
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}